import { useFetcher } from '@remix-run/react';
import { ChevronLeft, ChevronRight, FileText, Paperclip, Pencil, RotateCw, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { InternalNotesPanel } from '~/components/referrals/InternalNotesPanel';
import { RotatingOverlay } from '~/components/ui/RotatingOverlay';
import { useToast } from '~/components/ui/ToastContext';
import type {
  DayOperationsData,
  DayOperationsReferral,
  DayOperationsXRay,
} from '~/types/day-operations';
import type { DentalSpecialty } from '~/types/specialty';
import {
  formatDateToLocalString,
  formatDoctorName,
  formatToStandardDate,
  parseLocalDate,
} from '~/utils/formatters';
import { calculateAge } from '~/utils/patient-helpers';
import { formatSpecialtyReason } from '~/utils/referral-helpers';
import { retainKeys, sameObjectUrl } from '~/utils/stable-signed-url';
import { formatToothOrAreaWithInitials, isPediatricTooth } from '~/utils/tooth-helpers';
import { rotateExistingXray } from '~/utils/xray-rotate-existing';
import { isXrayRotatable } from '~/utils/xray-rotation-eligible';

type DayOperationsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

function formatReferredFor(referral: DayOperationsReferral): string {
  if (!referral.reasonGroups?.length) return '—';
  const specialty = referral.specialty as DentalSpecialty;
  const parts = referral.reasonGroups.map(group => {
    const label = formatSpecialtyReason(specialty, group.reasonCode);
    const teeth = (group.selectedTeeth ?? [])
      .filter((t: number) => !isPediatricTooth(t))
      .sort((a: number, b: number) => a - b)
      .map((t: number) => formatToothOrAreaWithInitials(t));
    const pediatricTeeth = [
      ...(group.selectedTeeth ?? [])
        .filter((t: number) => isPediatricTooth(t))
        .map((t: number) => formatToothOrAreaWithInitials(t)),
      ...(group.selectedPediatricTeeth ?? []),
    ]
      .filter((v, i, a) => a.indexOf(v) === i) // Unique
      .sort((a: string, b: string) => a.localeCompare(b));
    const teethStr = [...teeth, ...pediatricTeeth].filter(Boolean).join(', ');
    if (teethStr) return `${label}: ${teethStr}`;
    return label;
  });
  return parts.join('; ');
}

export function PatientInfoPanel({
  referral,
  onClose,
  onEditReasons,
  className = 'absolute top-4 left-4 z-10',
}: {
  referral: DayOperationsReferral;
  onClose: () => void;
  onEditReasons: () => void;
  className?: string;
}) {
  const patientName = `${referral.patientFirstName} ${referral.patientLastName}`;
  const dob = referral.patientDob ? new Date(referral.patientDob) : null;
  const age = dob ? calculateAge(dob) : null;
  const dobFormatted = dob ? formatToStandardDate(dob) : '';

  return (
    <div
      className={`w-96 rounded-lg border border-gray-600 bg-gray-900/95 p-4 shadow-xl ${className}`.trim()}
    >
      <button
        onClick={onClose}
        className="absolute right-2 top-2 text-gray-400 hover:text-white"
        aria-label="Close panel"
      >
        <X className="h-4 w-4" />
      </button>
      <h3 className="mb-3 text-sm font-semibold text-white">Patient & Referring Info</h3>
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 text-sm sm:grid-cols-2">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Patient Name</p>
          <p className="font-medium text-white">
            {patientName}
            {referral.status === 'CONSULTATION_SCHEDULED' && (
              <span className="ml-2 rounded-full bg-cyan-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-cyan-200">
                Consultation
              </span>
            )}
          </p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">DOB</p>
          <p className="text-gray-200">
            {dobFormatted}
            {age !== null && ` (Age ${age})`}
          </p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
            Referring Office
          </p>
          <p className="text-gray-200">{referral.referringOfficeName || '—'}</p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
            Referring Doctor
          </p>
          <p className="text-gray-200">{referral.referringProviderName || '—'}</p>
        </div>
        <div className="sm:col-span-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
              Referred for
            </p>
            <button
              onClick={onEditReasons}
              className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300"
            >
              <Pencil className="h-3 w-3" />
              Edit
            </button>
          </div>
          <p className="text-gray-200">{formatReferredFor(referral)}</p>
        </div>
      </div>
    </div>
  );
}

function EditReasonForm({
  referralId,
  group,
  specialty,
  onCancel,
  onSaved,
  onLocalSave,
}: {
  referralId: string;
  group: DayOperationsReferral['reasonGroups'][number];
  specialty: DentalSpecialty;
  onCancel: () => void;
  onSaved: () => void;
  /** Demo Builder: save in memory instead of posting to the referral route. Default off. */
  onLocalSave?: (data: { selectedTeeth: string; diagnosis: string; notes: string }) => void;
}) {
  const fetcher = useFetcher<{ success?: boolean; error?: string }>();
  const [formData, setFormData] = useState({
    selectedTeeth: [
      ...(group.selectedTeeth ?? [])
        .filter((t: number) => !isPediatricTooth(t))
        .sort((a: number, b: number) => a - b)
        .map((t: number) => formatToothOrAreaWithInitials(t)),
      ...(group.selectedTeeth ?? [])
        .filter((t: number) => isPediatricTooth(t))
        .map((t: number) => formatToothOrAreaWithInitials(t)),
      ...(group.selectedPediatricTeeth ?? []),
    ]
      .filter((v, i, a) => a.indexOf(v) === i)
      .join(', '),
    diagnosis: (group.diagnosis ?? []).join('\n'),
    notes: group.notes ?? '',
  });

  const isSaving = fetcher.state !== 'idle';

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data?.success) {
      onSaved();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher.state, fetcher.data?.success]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onLocalSave) {
      onLocalSave(formData);
      onSaved();
      return;
    }
    const fd = new FormData();
    fd.set('actionType', 'edit-reason-group');
    fd.set('reasonGroupId', group.id);
    fd.set('selectedTeeth', formData.selectedTeeth);
    fd.set('diagnosis', formData.diagnosis);
    fd.set('notes', formData.notes);
    fetcher.submit(fd, { method: 'POST', action: `/referrals/${referralId}` });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-md bg-gray-800 p-3 shadow-inner">
      <div className="flex items-center justify-between border-b border-gray-700 pb-2">
        <h4 className="text-sm font-semibold text-purple-300">
          {formatSpecialtyReason(specialty, group.reasonCode)}
        </h4>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="text-xs text-gray-400 hover:text-white"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="rounded bg-purple-600 px-2 py-1 text-xs font-medium text-white hover:bg-purple-500 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-400">
            Teeth (e.g. 1, 2, A)
          </label>
          <input
            type="text"
            value={formData.selectedTeeth}
            onChange={e => setFormData({ ...formData, selectedTeeth: e.target.value })}
            className="w-full rounded border border-gray-600 bg-gray-700 px-2 py-1 text-xs text-white focus:border-purple-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-400">
            Diagnosis (line-sep)
          </label>
          <textarea
            rows={2}
            value={formData.diagnosis}
            onChange={e => setFormData({ ...formData, diagnosis: e.target.value })}
            className="w-full rounded border border-gray-600 bg-gray-700 px-2 py-1 text-xs text-white focus:border-purple-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-400">Internal Notes</label>
          <textarea
            rows={2}
            value={formData.notes}
            onChange={e => setFormData({ ...formData, notes: e.target.value })}
            className="w-full rounded border border-gray-600 bg-gray-700 px-2 py-1 text-xs text-white focus:border-purple-500 focus:outline-none"
          />
        </div>
      </div>
      {fetcher.data?.error && <p className="text-xs text-red-400">{fetcher.data.error}</p>}
    </form>
  );
}

export function EditReasonGroupPanel({
  referral,
  onClose,
  onSaved,
  onLocalSave,
  onStartEdit,
  onCancelEdit,
}: {
  referral: DayOperationsReferral;
  onClose: () => void;
  onSaved: () => void;
  onLocalSave?: (
    groupId: string,
    data: { selectedTeeth: string; diagnosis: string; notes: string }
  ) => void;
  /** Demo Builder: notify when a reason group enters edit mode. Default off. */
  onStartEdit?: () => void;
  /** Demo Builder: notify when edit is cancelled. Default off. */
  onCancelEdit?: () => void;
}) {
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);

  return (
    <div className="flex w-96 shrink-0 flex-col border-l border-gray-600 bg-gray-900">
      <div className="flex items-center justify-between border-b border-gray-600 px-3 py-2">
        <h3 className="text-sm font-semibold text-white">Edit Referral Reasons</h3>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-white"
          aria-label="Close edit panel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-3">
        <div className="space-y-6">
          {(referral.reasonGroups ?? []).map(group => {
            const isEditing = editingGroupId === group.id;
            const specialty = referral.specialty as DentalSpecialty;

            if (isEditing) {
              return (
                <EditReasonForm
                  key={group.id}
                  referralId={referral.id}
                  group={group}
                  specialty={specialty}
                  onCancel={() => {
                    setEditingGroupId(null);
                    onCancelEdit?.();
                  }}
                  onLocalSave={onLocalSave ? data => onLocalSave(group.id, data) : undefined}
                  onSaved={() => {
                    setEditingGroupId(null);
                    onSaved();
                  }}
                />
              );
            }

            return (
              <div
                key={group.id}
                className="group relative border-b border-gray-800 pb-4 last:border-0"
              >
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-200">
                    {formatSpecialtyReason(specialty, group.reasonCode)}
                  </h4>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingGroupId(group.id);
                      onStartEdit?.();
                    }}
                    className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300"
                  >
                    <Pencil className="h-3 w-3" />
                    Edit
                  </button>
                </div>

                <div className="mt-2 space-y-2 text-xs">
                  {group.selectedTeeth?.length > 0 || group.selectedPediatricTeeth?.length > 0 ? (
                    <div>
                      <span className="font-medium text-gray-400">Teeth: </span>
                      <span className="text-gray-300">
                        {[
                          ...(group.selectedTeeth ?? [])
                            .filter((t: number) => !isPediatricTooth(t))
                            .sort((a: number, b: number) => a - b)
                            .map((t: number) => formatToothOrAreaWithInitials(t)),
                          ...(group.selectedTeeth ?? [])
                            .filter((t: number) => isPediatricTooth(t))
                            .map((t: number) => formatToothOrAreaWithInitials(t)),
                          ...(group.selectedPediatricTeeth ?? []),
                        ]
                          .filter((v, i, a) => a.indexOf(v) === i)
                          .sort((a: string, b: string) => a.localeCompare(b))
                          .join(', ')}
                      </span>
                    </div>
                  ) : null}

                  {group.diagnosis?.length > 0 && (
                    <div>
                      <span className="font-medium text-gray-400">Diagnosis: </span>
                      <span className="text-gray-300">{group.diagnosis.join(', ')}</span>
                    </div>
                  )}

                  {group.notes && (
                    <div className="mt-1 rounded bg-gray-800/50 p-2 italic text-gray-400">
                      {group.notes}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Exported for unit testing of the rotation flow in isolation.
// The default consumer is DayOperationsModal in this same file.
export function XRayGallery({
  xrays,
  selectedIndex,
  onSelect,
}: {
  xrays: DayOperationsXRay[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}) {
  const toast = useToast();
  const [rotatingIds, setRotatingIds] = useState<Set<string>>(new Set());
  const [urlOverrides, setUrlOverrides] = useState<Map<string, string>>(new Map());
  // Pins the displayed signed URL per X-ray id so a refreshed SigV4 signature for
  // the SAME object does not change <img src> and force a re-fetch. (#1065)
  const pinnedSignedUrls = useRef<Map<string, string>>(new Map());

  // Prune optimistic rotation state ONLY for X-rays that are gone. Keyed on a
  // stable id signature (not the array reference) so a benign re-fetch — same
  // X-rays, freshly re-signed URLs — does NOT wipe an in-flight rotation. (#1065)
  // NUL delimiter to avoid any chance of id concatenation collisions.
  const xrayIdSignature = xrays.map(x => x.id).join('\u0000');
  useEffect(() => {
    const present = new Set(xrays.map(x => x.id));
    setUrlOverrides(prev => retainKeys(prev, present));
    // Also prune the pinned-URL ref (not React state) so it cannot grow unbounded
    // as X-rays come and go over a long-lived session. (#1065)
    for (const id of pinnedSignedUrls.current.keys()) {
      if (!present.has(id)) pinnedSignedUrls.current.delete(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- xrayIdSignature is the
    // intended stable trigger; xrays is read fresh inside.
  }, [xrayIdSignature]);

  // Note: called only when there's no active urlOverride for the id (override wins),
  // so the pin isn't refreshed mid-override — intentional; both paths render correctly.
  // Returns a stable signed URL for an id: the pinned URL when the fresh one points
  // at the same object (signature-only change), otherwise the fresh URL (re-pinned).
  const stableSignedUrl = (id: string, fresh: string): string => {
    const prev = pinnedSignedUrls.current.get(id);
    if (prev && sameObjectUrl(prev, fresh)) return prev;
    pinnedSignedUrls.current.set(id, fresh);
    return fresh;
  };

  if (xrays.length === 0) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-900 text-gray-500">
        <p>No X-rays attached</p>
      </div>
    );
  }

  const selectedXray = xrays[selectedIndex];
  const selectedSrc =
    urlOverrides.get(selectedXray.id) ?? stableSignedUrl(selectedXray.id, selectedXray.signedUrl);
  const selectedRotating = rotatingIds.has(selectedXray.id);

  async function handleRotate(xray: DayOperationsXRay) {
    if (rotatingIds.has(xray.id)) return;
    setRotatingIds(s => {
      const n = new Set(s);
      n.add(xray.id);
      return n;
    });
    try {
      // rotateExistingXray preloads the rotated URL, so the override swap below
      // lands on an already-cached image. No optimistic CSS rotation — clearing
      // it at the swap showed a brief unrotated frame; a spinner covers the wait. (#1073)
      const url = await rotateExistingXray(xray.id, xray.originalFilename ?? xray.filename);
      setUrlOverrides(m => {
        const n = new Map(m);
        n.set(xray.id, url);
        return n;
      });
    } catch (err) {
      console.error('Failed to rotate existing X-ray', err);
      toast.show({ type: 'error', message: "Couldn't rotate X-ray. Try again." });
    } finally {
      setRotatingIds(s => {
        const n = new Set(s);
        n.delete(xray.id);
        return n;
      });
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-gray-900 p-4">
        <img
          src={selectedSrc}
          alt={selectedXray.filename}
          className="max-h-full max-w-full object-contain"
        />
        {selectedRotating && <RotatingOverlay label="Rotating X-ray" size="lg" />}
      </div>
      {isXrayRotatable(selectedXray) && (
        // Dedicated control strip below the image. Putting the button in its
        // own row (instead of `absolute bottom-4` on the image) avoids
        // overlapping the X-ray content for portraits and other shapes that
        // fill the image container's full height.
        <div className="flex shrink-0 items-center justify-center bg-gray-900 pb-3 pt-1">
          <button
            type="button"
            onClick={() => handleRotate(selectedXray)}
            disabled={selectedRotating}
            aria-label="Rotate clockwise 90°"
            title="Rotate clockwise 90°"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-700/90 text-white hover:bg-gray-600 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <RotateCw className="h-4 w-4" />
          </button>
        </div>
      )}
      {xrays.length > 1 && (
        <div className="flex shrink-0 justify-center gap-2 overflow-x-auto bg-gray-800/80 p-2">
          {xrays.map((xray, idx) => {
            const thumbSrc = urlOverrides.get(xray.id) ?? stableSignedUrl(xray.id, xray.signedUrl);
            return (
              <button
                key={xray.id}
                onClick={() => onSelect(idx)}
                className={`h-16 w-16 shrink-0 overflow-hidden rounded border-2 transition-colors ${
                  idx === selectedIndex
                    ? 'border-purple-500'
                    : 'border-transparent hover:border-gray-500'
                }`}
              >
                <img src={thumbSrc} alt={xray.filename} className="h-full w-full object-cover" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function DayOperationsModal({ isOpen, onClose }: DayOperationsModalProps) {
  const fetcher = useFetcher<DayOperationsData>();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showInfoPanel, setShowInfoPanel] = useState(true);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [showEditReasonsPanel, setShowEditReasonsPanel] = useState(false);
  const [selectedXrayIndex, setSelectedXrayIndex] = useState(0);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(() => formatDateToLocalString(new Date()));

  const data = fetcher.data as DayOperationsData | { error?: string } | undefined;
  const referrals = data && 'referrals' in data ? data.referrals : [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const doctors = data && 'doctors' in data ? data.doctors : [];
  const currentUserId = data && 'currentUserId' in data ? data.currentUserId : null;
  const receivingOfficeLogoUrl =
    data && 'receivingOfficeLogoUrl' in data ? data.receivingOfficeLogoUrl : null;
  const error = data && 'error' in data ? data.error : null;
  const currentReferral = referrals[currentIndex];

  const loadData = useCallback(() => {
    if (!isOpen) return;
    const params = new URLSearchParams();
    if (selectedDoctorId) params.set('doctorId', selectedDoctorId);
    params.set('date', selectedDate);
    fetcher.load(`/api/day-operations?${params.toString()}`);
  }, [isOpen, selectedDoctorId, selectedDate, fetcher]);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedDoctorId, selectedDate]);

  const hasSetInitialDoctor = useRef(false);
  useEffect(() => {
    if (doctors.length > 0 && !selectedDoctorId && !hasSetInitialDoctor.current) {
      hasSetInitialDoctor.current = true;
      // Default to current user if they're in the list, otherwise first doctor
      const defaultDoctorId =
        currentUserId && doctors.some(d => d.id === currentUserId) ? currentUserId : doctors[0].id;
      setSelectedDoctorId(defaultDoctorId);
    }
  }, [doctors, selectedDoctorId, currentUserId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') {
        setCurrentIndex(prev => (prev > 0 ? prev - 1 : referrals.length - 1));
        setSelectedXrayIndex(0);
      }
      if (e.key === 'ArrowRight') {
        setCurrentIndex(prev => (prev < referrals.length - 1 ? prev + 1 : 0));
        setSelectedXrayIndex(0);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, referrals.length]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-900">
      <header className="flex items-center justify-between border-b border-gray-700 bg-gray-800 px-6 py-3">
        <div className="flex items-center gap-4">
          {receivingOfficeLogoUrl && (
            <img
              src={receivingOfficeLogoUrl}
              alt="Receiving office"
              className="h-8 w-auto object-contain"
            />
          )}
          <h2 className="text-lg font-semibold text-white">Provider's Day-Prep Station</h2>
          <div className="flex items-center gap-2">
            <select
              value={selectedDoctorId || ''}
              onChange={e => {
                const id = e.target.value;
                setSelectedDoctorId(id);
                const params = new URLSearchParams();
                if (id) params.set('doctorId', id);
                params.set('date', selectedDate);
                fetcher.load(`/api/day-operations?${params.toString()}`);
              }}
              className="rounded border border-gray-600 bg-gray-700 px-3 py-1.5 text-sm text-white"
            >
              {doctors.map(d => (
                <option key={d.id} value={d.id}>
                  {formatDoctorName(d.firstName, d.lastName)}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-300">
              Today: {formatToStandardDate(parseLocalDate(selectedDate) ?? new Date())}
            </span>
            <input
              type="date"
              value={selectedDate}
              onChange={e => {
                const date = e.target.value;
                setSelectedDate(date);
                const params = new URLSearchParams();
                if (selectedDoctorId) params.set('doctorId', selectedDoctorId);
                params.set('date', date);
                fetcher.load(`/api/day-operations?${params.toString()}`);
              }}
              className="rounded border border-gray-600 bg-gray-700 px-2 py-1 text-sm text-white"
            />
            {referrals.length > 0 && (
              <select
                value={currentIndex}
                onChange={e => {
                  const idx = Number(e.target.value);
                  setCurrentIndex(idx);
                  setSelectedXrayIndex(0);
                }}
                className="min-w-[12rem] rounded border border-gray-600 bg-gray-700 px-3 py-1.5 text-sm text-white"
                aria-label="Select patient"
              >
                {referrals.map((r, idx) => {
                  const name = `${r.patientFirstName} ${r.patientLastName}`;
                  const dobStr = r.patientDob ? formatToStandardDate(new Date(r.patientDob)) : '';
                  const statusSuffix =
                    r.status === 'CONSULTATION_SCHEDULED' ? ' — Consultation' : '';
                  return (
                    <option key={r.id} value={idx}>
                      {name} ({dobStr}){statusSuffix}
                    </option>
                  );
                })}
              </select>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-800"
          aria-label="Close"
        >
          Close
        </button>
      </header>

      <div className="relative flex flex-1 overflow-hidden">
        {fetcher.state === 'loading' && !data ? (
          <div className="flex flex-1 items-center justify-center text-gray-400">Loading...</div>
        ) : error ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-red-400">
            <p className="text-lg">Failed to load day operations</p>
            <p className="text-sm">{error}</p>
          </div>
        ) : referrals.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-gray-400">
            <p className="text-lg">No scheduled appointments for this day</p>
            <p className="text-sm">Use the Schedule button on referrals to add appointments</p>
          </div>
        ) : (
          <>
            <div className="flex min-w-0 flex-1">
              <div className="relative flex min-w-0 flex-1 flex-col">
                <button
                  onClick={() => {
                    setCurrentIndex(prev => (prev > 0 ? prev - 1 : referrals.length - 1));
                    setSelectedXrayIndex(0);
                  }}
                  className="absolute left-4 top-1/2 z-20 -translate-y-1/2 rounded-full bg-gray-700/90 p-2 text-white hover:bg-gray-600"
                  aria-label="Previous"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                {showInfoPanel && currentReferral && (
                  <PatientInfoPanel
                    referral={currentReferral}
                    onClose={() => setShowInfoPanel(false)}
                    onEditReasons={() => {
                      setShowEditReasonsPanel(true);
                      setShowNotesPanel(false);
                    }}
                  />
                )}
                {!showInfoPanel && (
                  <button
                    onClick={() => setShowInfoPanel(true)}
                    className="absolute top-4 left-4 z-10 rounded bg-gray-700/90 px-3 py-1.5 text-sm text-white hover:bg-gray-600"
                  >
                    Show Patient Info
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowNotesPanel(prev => !prev)}
                  className={`absolute top-4 right-4 z-10 flex items-center gap-2 rounded px-3 py-1.5 text-sm ${
                    showNotesPanel
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-700/90 text-white hover:bg-gray-600'
                  }`}
                  aria-label={
                    showNotesPanel ? 'Hide administrative notes' : 'Show administrative notes'
                  }
                >
                  <FileText className="h-4 w-4" />
                  Administrative Notes
                  {currentReferral && currentReferral.unreadMentionNoteCount > 0 && (
                    <span className="ml-1 inline-flex items-center rounded-full bg-purple-300 px-1.5 text-xs font-semibold text-purple-900">
                      {currentReferral.unreadMentionNoteCount}
                    </span>
                  )}
                </button>

                <div className="flex min-h-0 flex-1 flex-col">
                  <XRayGallery
                    xrays={currentReferral?.xrays ?? []}
                    selectedIndex={selectedXrayIndex}
                    onSelect={setSelectedXrayIndex}
                  />
                </div>

                {currentReferral?.documents && currentReferral.documents.length > 0 && (
                  <div className="absolute bottom-4 left-4">
                    <div className="flex flex-col gap-2">
                      {currentReferral.documents.map(doc => (
                        <a
                          key={doc.id}
                          href={doc.signedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded bg-gray-700/90 px-3 py-2 text-sm text-white hover:bg-gray-600"
                        >
                          <Paperclip className="h-4 w-4" />
                          {doc.filename}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <button
                  onClick={() => {
                    setCurrentIndex(prev => (prev < referrals.length - 1 ? prev + 1 : 0));
                    setSelectedXrayIndex(0);
                  }}
                  className="absolute right-4 top-1/2 z-20 -translate-y-1/2 rounded-full bg-gray-700/90 p-2 text-white hover:bg-gray-600"
                  aria-label="Next"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>

              {showEditReasonsPanel && currentReferral && (
                <EditReasonGroupPanel
                  referral={currentReferral}
                  onClose={() => setShowEditReasonsPanel(false)}
                  onSaved={() => {
                    loadData();
                  }}
                />
              )}

              {showNotesPanel && currentReferral && (
                <InternalNotesPanel
                  referralId={currentReferral.id}
                  initialNotes={currentReferral.internalNotes}
                  unreadMentionNoteCount={currentReferral.unreadMentionNoteCount}
                  onClose={() => setShowNotesPanel(false)}
                  onMarkedRead={loadData}
                />
              )}
            </div>
          </>
        )}
      </div>

      {referrals.length > 0 && (
        <footer className="border-t border-gray-700 bg-gray-800 px-6 py-2 text-center text-sm text-gray-400">
          {currentIndex + 1} of {referrals.length}
        </footer>
      )}
    </div>
  );
}
